import { supabase } from '../lib/supabase'

/** Código de Postgres para violación de unicidad (tipo ya en otra cola, nombre repetido). */
const UNIQUE_VIOLATION = '23505'

const QUEUE_COLUMNS =
  'id,name,description,color,strategy,active,createdAt,queueType(type),queueMember(employeeId,active)'

/**
 * Normaliza una cola con sus relaciones embebidas a `types` y `memberIds`.
 * @param {Object} row
 * @returns {Object}
 */
const mapQueue = ({ queueType, queueMember, ...queue }) => ({
  ...queue,
  types: (queueType || []).map((t) => t.type),
  memberIds: (queueMember || [])
    .filter((m) => m.active)
    .map((m) => m.employeeId),
})

/** Traduce errores de la base a mensajes para la UI. */
const friendlyError = (error) =>
  error?.code === UNIQUE_VIOLATION
    ? 'Otra cola ya usa ese nombre o alguno de esos tipos de registro.'
    : error?.message || 'Error inesperado'

/**
 * Colas de atención (`Tickets.queue`), sus tipos (`queueType`) y miembros
 * (`queueMember`). La asignación automática la hace un trigger de la base.
 * @namespace queueService
 */
const queueService = {
  schemaName: 'Tickets',
  tableName: 'queue',

  db() {
    return supabase.schema(this.schemaName)
  },

  /**
   * Lista las colas con sus tipos y miembros activos.
   * @returns {Promise<{success: boolean, data: Array|null, error: string|undefined}>}
   */
  async getAll() {
    try {
      const { data, error } = await this.db()
        .from(this.tableName)
        .select(QUEUE_COLUMNS)
        .order('name', { ascending: true })
      if (error) throw error
      return { success: true, data: (data || []).map(mapQueue) }
    } catch (error) {
      console.error('Error fetching queues:', error)
      return { success: false, data: null, error: friendlyError(error) }
    }
  },

  /**
   * Reemplaza los tipos de registro de una cola.
   * @param {number} queueId
   * @param {string[]} types
   */
  async setTypes(queueId, types = []) {
    const { error: deleteError } = await this.db()
      .from('queueType')
      .delete()
      .eq('queueId', queueId)
    if (deleteError) throw deleteError
    if (!types.length) return
    const { error } = await this.db()
      .from('queueType')
      .insert(types.map((type) => ({ type, queueId })))
    if (error) throw error
  },

  /**
   * Reemplaza los miembros de una cola.
   * @param {number} queueId
   * @param {string[]} memberIds - ids de `public.Empleados`.
   */
  async setMembers(queueId, memberIds = []) {
    const { error: deleteError } = await this.db()
      .from('queueMember')
      .delete()
      .eq('queueId', queueId)
    if (deleteError) throw deleteError
    const unique = [...new Set(memberIds.filter(Boolean))]
    if (!unique.length) return
    const { error } = await this.db()
      .from('queueMember')
      .insert(unique.map((employeeId) => ({ queueId, employeeId })))
    if (error) throw error
  },

  /**
   * Crea una cola con sus tipos y miembros.
   * @param {Object} draft - name, description, color, strategy, active, types, memberIds.
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async create({ types, memberIds, ...queue }) {
    try {
      const { data, error } = await this.db()
        .from(this.tableName)
        .insert(queue)
        .select('id')
        .single()
      if (error) throw error
      await this.setTypes(data.id, types)
      await this.setMembers(data.id, memberIds)
      return { success: true, data }
    } catch (error) {
      console.error('Error creating queue:', error)
      return { success: false, data: null, error: friendlyError(error) }
    }
  },

  /**
   * Actualiza una cola y reemplaza sus tipos y miembros.
   * @param {number} id
   * @param {Object} draft
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async update(id, { types, memberIds, ...queue }) {
    try {
      const { error } = await this.db()
        .from(this.tableName)
        .update(queue)
        .eq('id', id)
      if (error) throw error
      await this.setTypes(id, types)
      await this.setMembers(id, memberIds)
      return { success: true, data: { id } }
    } catch (error) {
      console.error('Error updating queue:', error)
      return { success: false, data: null, error: friendlyError(error) }
    }
  },

  /**
   * Activa o desactiva una cola (sin tocar tipos ni miembros).
   * @param {number} id
   * @param {boolean} active
   */
  async setActive(id, active) {
    try {
      const { error } = await this.db()
        .from(this.tableName)
        .update({ active })
        .eq('id', id)
      if (error) throw error
      return { success: true, data: { id, active } }
    } catch (error) {
      console.error('Error toggling queue:', error)
      return { success: false, data: null, error: friendlyError(error) }
    }
  },

  /**
   * Elimina una cola. Sus tipos y miembros caen en cascada y los tickets que
   * la tenían quedan con `queueId` nulo (conservan su responsable).
   * @param {number} id
   */
  async remove(id) {
    try {
      const { error } = await this.db()
        .from(this.tableName)
        .delete()
        .eq('id', id)
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting queue:', error)
      return { success: false, error: friendlyError(error) }
    }
  },
}

export default queueService
